import { DomainError, NotExistError } from 'scripts/application/lib/error';
import handleTeamAssistV2Error from 'actions/team_assist_v2/lib/handle_team_assist_v2_error';
import isTeamAssistV2Enabled from 'actions/team_assist_v2/lib/is_team_assist_v2_enabled';
import {
  proposalStatus,
  TeamAssistV2ProposalDecision,
  TeamAssistV2ProposalStatus,
} from 'actions/team_assist_v2/lib/team_assist_v2_answer';
import { findProposalBlock, writeProposal } from 'actions/team_assist_v2/lib/team_assist_v2_proposals';
import RequestTeamAssistV2 from 'actions/team_assist_v2/request_team_assist_v2';

/**
 * Declines a pending proposed action; nothing runs and no answer follows. The
 * card shows it rejected once glados records it. When glados says the action
 * ran or is gone, the session loads again to show what became of it.
 */
export default class RejectTeamAssistV2Proposal {
  constructor(context) {
    this.context = context;
  }

  run({ customerId, turnId, toolUseId }) {
    if (!isTeamAssistV2Enabled(this.context.stores.appFeatures.get())) {
      return;
    }
    const store = this.storeFor(customerId);
    if (!store || !store.has({ id: turnId })) {
      return;
    }
    const block = findProposalBlock(store.find(turnId), toolUseId);
    // Reject leaves the proposal pending until glados answers, so a second
    // press would send it again.
    if (!block || proposalStatus(block) !== TeamAssistV2ProposalStatus.pending || rejecting.has(toolUseId)) {
      return;
    }
    rejecting.add(toolUseId);
    this.context.analytics.track('Team Assist Proposed Action Rejected', {
      customerId,
      teamAssistVersion: 'v2',
      toolName: block.name,
    });

    return this.context.gateways.teamAssistV2
      .rejectProposal({ toolUseId })
      .then(
        () => {
          const current = this.storeFor(customerId);
          if (current) {
            writeProposal(current, turnId, toolUseId, proposal => ({
              ...proposal,
              decision: TeamAssistV2ProposalDecision.rejected,
            }));
          }
        },
        err => {
          if (err instanceof NotExistError || isAlreadyExecuted(err)) {
            this.context.executeAction(RequestTeamAssistV2, { customerId, isRefreshing: true });
            return;
          }
          handleTeamAssistV2Error(this.context, err, { isInteractive: true });
        }
      )
      .finally(() => rejecting.delete(toolUseId));
  }

  storeFor(customerId) {
    if (!customerId || !this.context.stores.customers.has({ id: customerId })) {
      return null;
    }
    return this.context.stores.customers.storesFor(customerId).teamAssistsV2;
  }
}

// The proposals whose Reject glados hasn't answered yet.
const rejecting = new Set();

function isAlreadyExecuted(err) {
  return err instanceof DomainError && err.errors.some(e => e.code === 'already_executed');
}
